import React, { useEffect, useMemo, useState } from "react";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useCart } from "@/state/CartContext";
import { useCatalog } from "@/state/CatalogContext";
import { inCategory } from "@/lib/categories";
import { mediaUrl } from "@/lib/api";
import { formatUzs } from "@/lib/format";
import { DI, GROUP_KINDS, flyToCart } from "./desktopUi";
import type { CategoryRef, Product } from "@/types";

/** Which menu is open: a coffee category slug, or an equipment / accessories group pseudo-slug. */
export type MegaKey = string;

const popularity = (p: Product) => (p.soldLastWeek ?? 0) * 10 + (p.tags?.includes("bestseller") ? 50 : 0) + (p.rating ?? 0);

/**
 * Big menu of the desktop header: hovering "Кофе", "Оборудование"… opens a panel
 * with the sections and their product counts, three popular products of the
 * section and the "pick of the month" card. Clicking the header item still
 * opens the catalogue as before.
 */
export function MegaMenu({ open, sections, groupSlug, onClose, onEnter }: {
  /** hovered header item */
  open: MegaKey;
  /** sections listed on the left (all coffee categories, or the group's categories) */
  sections: CategoryRef[];
  /** set when the hovered item is a whole group (equipment / accessories) */
  groupSlug: string | null;
  onClose: () => void;
  onEnter: () => void;
}) {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const { products } = useCatalog();
  const { addItem } = useCart();
  const [hover, setHover] = useState<string | null>(null);
  useEffect(() => setHover(null), [open]);
  // sits right under the header, wherever the header is (an announcement bar can push it down)
  const [top] = useState(() => Math.max(0, document.querySelector(".dk-header")?.getBoundingClientRect().bottom ?? 79));
  const current = hover ?? (groupSlug ? null : open);

  const inGroup = (p: Product) => (groupSlug ? GROUP_KINDS[groupSlug]?.includes(p.category) : sections.some((s) => inCategory(p, s.slug)));
  const pool = useMemo(() => products.filter(inGroup), [products, open, sections]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = useMemo(() => (current ? pool.filter((p) => inCategory(p, current)) : pool), [pool, current]);
  // the "pick of the month" card: the product marked so by the shop, else the best-rated one of the section
  const pick = useMemo(() => pool.find((p) => p.pickOfMonth) ?? [...pool].sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0) || popularity(b) - popularity(a))[0], [pool]);
  const popular = useMemo(() => {
    const sorted = [...shown].sort((a, b) => popularity(b) - popularity(a));
    const rest = sorted.filter((p) => p !== pick);
    return (rest.length >= 3 ? rest : sorted).slice(0, 3);
  }, [shown, pick]);

  const go = (route: Parameters<typeof navigate>[0]) => {
    onClose();
    navigate(route);
  };
  const add = (p: Product, el: Element) => {
    const v = p.variants.find((x) => x.stock > 0) ?? p.variants[0];
    if (!v || p.orderMode !== "buy_now" || v.stock <= 0) return go({ name: "product", slug: p.slug });
    addItem(p.id, v.id, 1);
    flyToCart(el);
  };
  const allSlug = groupSlug ?? (sections.length === 1 ? sections[0].slug : open);
  const notes = (p: Product) => (p.flavor?.notes ?? []).slice(0, 2).map((n) => t(`flavor.notes.${n}`));
  const sub = (p: Product) => (notes(p).length ? null : p.brand || p.shortDescription);

  return (
    <div className="mm" style={{ top }} onMouseEnter={onEnter} onMouseLeave={onClose} role="region" aria-label={t("desktop.catalog")}>
      <div className="mm__in">
        <div className="mm__col">
          <div className="mm__h">{groupSlug ? t(groupSlug.includes("equipment") ? "desktop.equipment" : "desktop.accessories") : t("catalog.categories.coffee")}</div>
          {sections.map((s) => {
            const n = products.filter((p) => inCategory(p, s.slug)).length;
            return (
              <button key={s.id} className="mm__sec" data-on={current === s.slug} onMouseEnter={() => setHover(s.slug)} onClick={() => go({ name: "catalog", category: s.slug })}>
                <span>{s.name}</span>
                <small>{n}</small>
              </button>
            );
          })}
          <button className="mm__all" onClick={() => go({ name: "catalog", category: allSlug })}>{groupSlug ? t("home.seeAll") : t("desktop.allCoffee")} {DI.arrow}</button>
          {!groupSlug && <button className="mm__sec mm__sec--fw" onClick={() => go({ name: "flavors" })}><span>{t("desktop.fwTitle")}</span><small>✦</small></button>}
        </div>

        <div className="mm__mid">
          <div className="mm__h">{t("desktop.mmPopular")}</div>
          <div className="mm__cards">
            {popular.map((p) => {
              const v = p.variants[0];
              return (
                <div key={p.id} className="mm__card" onClick={() => go({ name: "product", slug: p.slug })}>
                  <div className="mm__img">{p.images[0] && <img src={mediaUrl(p.images[0], 400)} alt={p.name} loading="lazy" />}</div>
                  <b>{p.name}</b>
                  <div className="mm__notes">
                    {notes(p).map((n) => <span key={n}>{n}</span>)}
                    {sub(p) && <em>{sub(p)}</em>}
                  </div>
                  <div className="mm__row">
                    <span>{v && v.priceUzs > 0 ? formatUzs(v.priceUzs, locale) : t("catalog.onRequest")}</span>
                    <button
                      className="mm__add"
                      aria-label={p.orderMode === "buy_now" ? t("product.addToCart") : t("desktop.qvMore")}
                      onClick={(e) => {
                        e.stopPropagation();
                        add(p, e.currentTarget);
                      }}
                    >
                      {p.orderMode === "buy_now" ? "+" : DI.arrow}
                    </button>
                  </div>
                </div>
              );
            })}
            {popular.length === 0 && <div className="mm__empty">{t("desktop.nothingFound")}</div>}
          </div>
        </div>

        {pick && (
          <div className="mm__pick" onClick={() => go({ name: "product", slug: pick.slug })}>
            <div className="mm__pickeb">{t("common.pickOfMonth")}</div>
            <h3 className="dk-serif">{pick.name}</h3>
            <p>{notes(pick).length ? notes(pick).join(" · ") : pick.shortDescription}</p>
            {pick.images[0] && <span className="mm__pickimg"><img src={mediaUrl(pick.images[0], 400)} alt="" /></span>}
            <span className="mm__go">{t("desktop.mmTry")} {DI.arrow}</span>
          </div>
        )}
      </div>
    </div>
  );
}

